import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { backend_api } from "../../../../static/server";
import {
  createTaskType,
  getAllTaskType,
  updateTaskOrType,
} from "../../../../redux/Apps/Lawyerapp/TaskSlice/TaskSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useDialog } from "../../../../components/DialogContext";
import { LoadingButton } from "@mui/lab";

const CreateTaskTypeForm = ({ isEditMode, editTaskTypeId, formData }) => {
  const { closeDialog } = useDialog();
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { token } = useSelector((data) => data.user);
  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      type_name: "",
      description: "",
      email_remind_day_before: [],
      email_remind_week_before: [],
      email_remind_month_before: [],
      enable_reminder_mail: false,
    },
  });

  const enableReminderMail = watch("enable_reminder_mail");
  
  useEffect(() => {
    if (isEditMode && formData) {
      setValue("type_name", formData.type_name);
      setValue("description", formData.description);
      setValue("email_remind_day_before", formData.email_remind_day_before);
      setValue("email_remind_week_before", formData.email_remind_week_before);
      setValue("email_remind_month_before", formData.email_remind_month_before);
      setValue("enable_reminder_mail", formData.enable_reminder_mail);
    }
  }, [isEditMode, formData]);

  const onSubmit = async (data) => {
    try {
      isEditMode
        ? await dispatch(
            updateTaskOrType({
              url: `${backend_api}task/register/${editTaskTypeId}/`,
              body: data,
              token: token,
            })
          ).unwrap()
        : await dispatch(
            createTaskType({
              url: `${backend_api}task/register/`,
              body: data,
              token: token,
            })
          ).unwrap();
      toast.success(
        isEditMode ? "Task type edited" : "Task type created successfully!"
      );
      await dispatch(
        getAllTaskType({ url: `${backend_api}task/register/`, token: token })
      ).unwrap();
    } catch (error) {
      toast.error(
        error?.message || isEditMode
          ? "Failed to edit task type"
          : "Failed to create task type"
      );
    } finally {
      closeDialog();
      reset();
    }
  };
  return (
    <>
      <Box>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              p: "1rem",
            }}
          >
            <Controller
              name="type_name"
              control={control}
              rules={{ required: "Type Name is required" }}
              render={({ field }) => (
                <TextField
                  {...field}
                  size="small"
                  fullWidth
                  label={
                    <span>
                      Task Name <span style={{ color: "red" }}>*</span>
                    </span>
                  }
                  error={!!errors.type_name}
                  helperText={errors.type_name?.message}
                />
              )}
            />
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  multiline
                  rows={3}
                  fullWidth
                  label="Description"
                />
              )}
            />
            <Box>
              <Divider orientation="horizontal" />
              <Accordion
                expanded={enableReminderMail || isExpanded}
                onChange={(e, expanded) => {
                  if (enableReminderMail) setIsExpanded(expanded);
                }}
                sx={{
                  borderRadius: "0.6rem",
                  width: "100%",
                  boxShadow: "none",
                  "&:before": {
                    display: "none",
                  },
                }}
              >
                <AccordionSummary sx={{ px: "0rem" }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Typography
                      variant="body1"
                      sx={{ fontSize: "1rem", fontWeight: 500 }}
                    >
                      Email reminder
                    </Typography>
                    <Box>
                      <Controller
                        name="enable_reminder_mail"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <Switch
                            size="small"
                            checked={value}
                            onChange={(e) => {
                              onChange(e.target.checked);
                              if (e.target.checked) {
                                setIsExpanded(true);
                              }
                            }}
                            sx={{
                              "& .MuiSwitch-switchBase.Mui-checked": {
                                color: "#00B3B0",
                              },
                              "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                                {
                                  backgroundColor: "#00B3B0",
                                },
                            }}
                          />
                        )}
                      />
                    </Box>
                  </Box>
                </AccordionSummary>
                <AccordionDetails sx={{ p: "0rem" }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "1rem",
                    }}
                  >
                    <Controller
                      name="email_remind_day_before"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth disabled={!enableReminderMail}>
                          <Autocomplete
                            disableCloseOnSelect
                            multiple
                            options={[1, 2, 3, 4, 5, 6]}
                            getOptionLabel={(option) => option.toString()}
                            value={field.value || []}
                            onChange={(_, newValue) => field.onChange(newValue)}
                            disabled={!enableReminderMail}
                            limitTags={5}
                            renderTags={(option, getoptionProps) =>
                              option?.map((option, index) => (
                                <Chip
                                  size="small"
                                  key={index}
                                  label={`Day ${option}`}
                                  {...getoptionProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Day Before"
                                size="small"
                              />
                            )}
                          />
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="email_remind_week_before"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth disabled={!enableReminderMail}>
                          <Autocomplete
                            limitTags={5}
                            disableCloseOnSelect
                            multiple
                            options={[1, 2, 3, 4]}
                            getOptionLabel={(option) => option.toString()}
                            value={field.value || []}
                            onChange={(_, newValue) => field.onChange(newValue)}
                            disabled={!enableReminderMail}
                            renderTags={(option, getoptionProps) =>
                              option.map((option, index) => (
                                <Chip
                                  size="small"
                                  key={index}
                                  label={`Week ${option}`}
                                  {...getoptionProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Weeks Before"
                                size="small"
                              />
                            )}
                          />
                        </FormControl>
                      )}
                    />
                    <Controller
                      name="email_remind_month_before"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth disabled={!enableReminderMail}>
                          <Autocomplete
                            limitTags={4}
                            disableCloseOnSelect
                            multiple
                            options={[1, 2, 3, 4, 5, 6]}
                            getOptionLabel={(option) => option.toString()}
                            value={field.value || []}
                            onChange={(_, newValue) => field.onChange(newValue)}
                            disabled={!enableReminderMail}
                            renderTags={(option, getoptionProps) =>
                              option?.map((option, index) => (
                                <Chip
                                  size="small"
                                  key={index}
                                  label={`Month ${option}`}
                                  {...getoptionProps({ index })}
                                />
                              ))
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Months Before"
                                size="small"
                              />
                            )}
                          />
                        </FormControl>
                      )}
                    />
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          </Box>
          <Box
            sx={{
              height: "fit-content",
              borderTop: "1px solid #ddd",
              position: "sticky",
              bottom: 0,
              backdropFilter: "blur(10px)",
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              p: "0.5rem",
              zIndex: 1000,
              gap: "0.5rem",
              bgcolor: "rgba(255, 255, 255, 0.8)",
            }}
          >
            <Button variant="outlined" size="small" onClick={closeDialog}>
              Cancel
            </Button>
            <LoadingButton
              loading={isSubmitting}
              variant="contained"
              color="primary"
              type="submit"
              size="small"
            >
              {isEditMode ? "Update Task Type" : "Create Task Type"}
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </>
  );
};

export default CreateTaskTypeForm;
